import { useInputBindingPress } from '@sorrem-client/ui/hooks';
import { PARTY_CHAT_MAX_LENGTH, type PartyChatMessage } from '@sorrem/shared';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import styles from './ChatPanel.module.scss';

interface ChatPanelProps {
    readonly isConnected: boolean;
    readonly chatMessages: readonly PartyChatMessage[];
    readonly onSendMessage: (body: string) => void;
}

/**
 * The party chat, the Chat tab beside Party in the party's HUD area: at the
 * gathering, and carried into the run (the party channel stays open through
 * it) so the chat stands in the same place on both. Messages float over the
 * world as shadowed text; the input is a bare line. Enter sends and Esc
 * leaves the input, handing the keys back to the game; the show-chat key
 * starts a message.
 */
export function ChatPanel({ isConnected, chatMessages, onSendMessage }: ChatPanelProps): JSX.Element {
    const [draft, setDraft] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    /*
     * Opening the tab, by click or key, starts a message. Focused on the next
     * frame, so the key that opened it isn't typed into the input.
     */
    const focusInput = (): void => {
        requestAnimationFrame(() => inputRef.current?.focus());
    };
    useEffect(focusInput, []);
    useInputBindingPress('show-chat', focusInput);

    // The newest message stays in view.
    useEffect(() => {
        const list = listRef.current;
        if (list !== null) {
            list.scrollTop = list.scrollHeight;
        }
    }, [chatMessages.length]);

    function handleSubmit(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();
        const body = draft.trim();
        if (body) {
            onSendMessage(body);
            setDraft('');
        }
        inputRef.current?.blur();
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if (event.key === 'Escape') {
            event.currentTarget.blur();
        }
    }

    return (
        <div className={styles.chatPanel}>
            {chatMessages.length === 0
                ? <p className={styles.status}>No messages yet.</p>
                : (
                    <ul ref={listRef} className={styles.chatList}>
                        {chatMessages.map((message) => (
                            <li key={message.id} className={styles.chatItem}>
                                <span className={styles.sender}>{message.characterName ?? message.displayName}</span>
                                <time className={styles.time} dateTime={message.sentAt}>
                                    {new Date(message.sentAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                </time>
                                <p className={styles.chatBody}>{message.body}</p>
                            </li>
                        ))}
                    </ul>
                )}

            <form className={styles.chatComposer} onSubmit={handleSubmit}>
                <input
                    ref={inputRef}
                    className={styles.input}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isConnected ? 'Message your party (Enter to send)' : 'Reconnecting...'}
                    aria-label="Message your party"
                    disabled={!isConnected}
                    maxLength={PARTY_CHAT_MAX_LENGTH}
                />
            </form>
        </div>
    );
}
